import { MembershipTemplate, GuestType } from "@/constants";
import React, { useMemo } from "react";
import { Card } from "./ui/card";
import { Check, CircleCheckBig, Loader2, Coins, ArrowRight, CreditCard } from "lucide-react";
import { Button } from "./ui/button";
import { useUser } from "@/hooks/use-user";
import { usePaymentMethods } from "@/hooks/use-payment-methods";
import { IS_TESTNET } from "@/constants/walletConnectionConfig";
import { useLocation } from "wouter";

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

export interface EnhancedMembershipTemplate {
	template: MembershipTemplate
	guestType: GuestType
	usdPrice: number
	sechPrice: number
	taxCalculation?: TaxCalculationResult
	totalSechPrice?: number
}

const AvailableMembershipCard: React.FC<{
	membership: EnhancedMembershipTemplate
	isSelected: boolean
	isCurrent: boolean
	canBuyToday: boolean
	isLoading?: boolean
	onSelect: (membership: EnhancedMembershipTemplate) => void
}> = (props) => {
	const { membership, isSelected, isLoading = false } = props;
	const [, navigate] = useLocation();

	const { walletAddress, balance } = useUser()
	const { cards, isCardExpired } = usePaymentMethods()

	// Check if user has at least one valid (non-expired, not flagged for re-entry) card on file
	const hasValidCard = useMemo(() => {
		return cards.some(card => !isCardExpired(card) && !card.updateRequired)
	}, [cards, isCardExpired])

	const { template } = membership

	const formattedDuration = useMemo(() => {
		const months = template.durationMonths
		if (months >= 12) {
			const years = months / 12
			return years === 1 ? '1 Year' : `${years} Years`
		}
		return months === 1 ? '1 Month' : `${months} Months`
	}, [template.durationMonths])

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	// Calculate final prices including tax
	const finalPrices = useMemo(() => {
		if (membership.taxCalculation) {
			return {
				usd: membership.taxCalculation.total,
				sech: membership.totalSechPrice || membership.sechPrice || 0
			}
		}
		return {
			usd: membership.usdPrice,
			sech: membership.sechPrice || 0
		}
	}, [membership.taxCalculation, membership.totalSechPrice, membership.sechPrice, membership.usdPrice])

	const onSelect = () => {
		if (!isLoading && membership.taxCalculation) {
			props.onSelect(membership);
		}
	}

	// Check if insufficient balance
	const hasInsufficientBalance = useMemo(() => {
		return walletAddress && balance !== undefined && balance < finalPrices.sech;
	}, [walletAddress, balance, finalPrices.sech]);

	// Calculate how much more SECH is needed
	const sechNeeded = useMemo(() => {
		if (hasInsufficientBalance && balance !== undefined) {
			return (finalPrices.sech - balance).toFixed(IS_TESTNET ? 6 : 2);
		}
		return '0';
	}, [hasInsufficientBalance, balance, finalPrices.sech]);

	const isPurchaseDisabled = useMemo(() => {
		const hasInsufficientSech = !balance || balance < finalPrices.sech;
		return !walletAddress ||
			(hasInsufficientSech && !hasValidCard) ||
			props.isCurrent ||
			!props.canBuyToday ||
			isLoading ||
			!membership.taxCalculation;
	}, [walletAddress, balance, props.isCurrent, props.canBuyToday, finalPrices.sech, isLoading, membership.taxCalculation, hasValidCard]);

	const buttonText = useMemo(() => {
		if (isLoading) return 'Calculating...';
		if (!walletAddress) return 'Sign in to proceed';
		if (props.isCurrent) return 'Active Membership';
		if (!props.canBuyToday) return 'Not available now';
		if (balance < finalPrices.sech && !hasValidCard) return 'Insufficient balance';
		return 'Select';
	}, [walletAddress, balance, props.isCurrent, props.canBuyToday, finalPrices.sech, isLoading, hasValidCard]);

	return <>
		<Card
			className={`flex flex-col h-full p-1 relative transition-all duration-200 bg-[#3C3C3C] rounded-[16px] border-0 ${isSelected
				? 'ring-2 ring-primary'
				: ''
				} ${isLoading ? 'opacity-75' : ''}`}>
			{isSelected && !isLoading && (
				<div className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full p-1 z-10">
					<Check className="h-4 w-4" />
				</div>
			)}

			<div className="bg-[linear-gradient(180.06deg,_#2F2F32_0.05%,_#2A2A2C_99.95%)] rounded-[12px] p-4 relative overflow-hidden flex flex-col flex-1">

				<div className="bg-[#709EFF] h-[220px] w-[220px] rounded-full blur-[37px] absolute z-[2] top-[-110px] left-1/2 -translate-x-1/2"></div>

				<div className="bg-[#FFB900] h-[220px] w-[220px] rounded-full absolute z-[1] blur-[70px] top-0 left-[-20px]"></div>
				<div className="bg-[#FF631A] h-[220px] w-[220px] rounded-full absolute z-[1] blur-[70px] top-12 left-[-123px]"></div>
				<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute blur-[70px] top-[130px] left-[-100px]"></div>

				<div className="bg-[#FFB900] h-[220px] w-[220px] rounded-full absolute blur-[70px] top-0 right-[-20px]"></div>
				<div className="bg-[#FF631A] h-[220px] w-[220px] rounded-full absolute blur-[70px] top-12 right-[-123px]"></div>
				<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute blur-[70px] top-[130px] right-[-100px]"></div>

				<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute left-1/2 -translate-x-1/2 top-[100px] z-[2] blur-[70px]"></div>
				<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute top-[133px] z-[1] left-[-54px] blur-[60px]"></div>
				<div className="bg-[#2F2F32] h-[220px] w-[220px] rounded-full absolute right-[-20px] top-[200px] z-[4] blur-[70px]"></div>

				<div className="text-start mb-4 relative z-[5] flex-col">
					<data className="text-[42px] leading-[42px] font-bold w-full text-start text-[#ffffff] font-figtree">
						${formatPrice(membership.usdPrice)}
					</data>
					<p className="text-[18px] leading-[22px] font-semibold font-figtree text-[#ffffff] mt-3 mb-3 w-full text-start">
						{template.name}
					</p>
					<div className="bg-[linear-gradient(271.96deg,_rgba(255,255,255,0.08)_0.59%,_rgba(255,255,255,0.08)_45.5%,_rgba(0,0,0,0.08)_96.23%)]
				  w-full rounded-[12px] p-[10px]
				  flex flex-col
				">
						{membership.sechPrice > 0 && (
							<span className="text-[18px] leading-5 font-semibold text-[#ffffff] font-figtree">
								{formatPrice(membership.sechPrice)} SECH
							</span>
						)}
					</div>

					<p className="text-[18px] leading-[22px] font-semibold font-figtree text-[#ffffff] mt-4 mb-1">
						{formattedDuration}
					</p>

					<div className="mt-2">
						{isLoading || !membership.taxCalculation ? (
							<div className="flex items-center justify-center">
								<Loader2 className="h-6 w-6 animate-spin mr-2" />
								<span className="text-lg">Calculating price...</span>
							</div>
						) : null}
					</div>
				</div>

				<ul className="relative z-[5] border-t border-[rgba(255,255,255,0.2)] pt-3 mb-4 flex-1">
					<li className="flex items-center text-sm mb-3">
						<CircleCheckBig className="h-4 w-4 mr-2 text-[#ffffff]" />
						<span className="text-[13px] leading-[15px] font-normal text-[#C7C7C7]">Full venue access</span>
					</li>
					<li className="flex items-center text-sm mb-3">
						<CircleCheckBig className="h-4 w-4 mr-2 text-[#ffffff]" />
						<span className="text-[13px] leading-[15px] font-normal text-[#C7C7C7]">Entry pass discounts</span>
					</li>
					<li className="flex items-center text-sm mb-3">
						<CircleCheckBig className="h-4 w-4 mr-2 text-[#ffffff]" />
						<span className="text-[13px] leading-[15px] font-normal text-[#C7C7C7]">Member-only events</span>
					</li>
				</ul>

				<div className="mt-auto">
				<Button
					className="w-full h-[42px] relative z-[5] bg-[#EF4444] rounded-[21px] flex items-center justify-center shadow-[0px_4px_36px_0px_#DC262699]"
					variant={
						isSelected
							? 'default'
							: 'secondary'
					}
					disabled={
						isPurchaseDisabled
					}
					onClick={onSelect}>
					{buttonText}
				</Button>

				{/* Buy SECH CTA when insufficient balance and no valid card */}
				{hasInsufficientBalance && !hasValidCard && !isLoading && membership.taxCalculation && (
					<>
						<button
							onClick={() => navigate('/buy-tokens')}
							className="w-full mt-3 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 hover:from-orange-600 hover:via-amber-600 hover:to-yellow-600 text-white rounded-[21px] p-3 shadow-md hover:shadow-lg transition-all duration-300 group relative overflow-hidden z-[5]"
						>
							{/* Animated shine effect */}
							<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>

							<div className="relative flex items-center justify-center gap-2">
								<Coins className="h-4 w-4"/>
								<span className="font-semibold text-sm">Buy SECH Tokens</span>
								<ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform"/>
							</div>
							<div className="relative text-white/90 text-xs mt-1">
								Need {sechNeeded} more SECH
							</div>
						</button>
						<button
							onClick={() => navigate('/account/payment-methods/add-card')}
							className="w-full mt-2 bg-[#2F2F32] hover:bg-[#3C3C3C] border border-[rgba(255,255,255,0.15)] text-white rounded-[21px] p-3 transition-all duration-300 group relative overflow-hidden z-[5]"
						>
							<div className="relative flex items-center justify-center gap-2">
								<CreditCard className="h-4 w-4"/>
								<span className="font-semibold text-sm">Add a Card</span>
								<ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform"/>
							</div>
							<div className="relative text-white/70 text-xs mt-1">
								Pay with credit or debit card
							</div>
						</button>
					</>
				)}
				</div>

			</div>

		</Card>
	</>
}

export default AvailableMembershipCard;
